/**
 * LLDP "Connected to" age (OP#1857, OP#1854 AC) -- PURE, unit-tested in topologyLayout.test.ts
 * (the lldpAge / agoLabel cases live there alongside the layout tests).
 *
 * The controller's lldp_* columns are a LAST-KNOWN snapshot: nothing clears them when a device
 * goes quiet, so an AP offline for 11 days still names its old switch. The API now sends the
 * snapshot's derived confirmation time (`as_of`, models/device.Device.lldp_as_of) and a `stale`
 * flag; this module turns them into the one phrase every screen shows next to the neighbour:
 *
 *   online + fresh   -> "as of 3m ago"         (normal text)
 *   online + stale   -> "as of 2h ago"         (muted: the device has not re-confirmed it)
 *   offline          -> "last seen 11d ago"    (muted: history, not a fact about now)
 *   no time basis    -> "age unknown"          (muted)
 */
import { parseApiTime } from "./format.ts";

/** The age-bearing subset of the API's `lldp` object (devices, alert rows, topology edges). */
export interface LldpAgeInput {
  as_of?: string | null;
  stale?: boolean | null;
}

export interface LldpAge {
  /** e.g. "as of 11d ago", "last seen 11d ago", "age unknown". */
  label: string;
  /** True when the neighbour must not read as current (muted text / dashed link). */
  stale: boolean;
}

/** Compact "N ago" from a timestamp: "just now", "12m ago", "3h ago", "11d ago". Floors. */
export function agoLabel(thenMs: number, nowMs: number): string {
  const secs = Math.max(0, Math.floor((nowMs - thenMs) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * The age phrase for an LLDP neighbour. `online` is the REPORTING device's state: an offline
 * device's neighbour is always "last seen" and always stale, whatever the flag says (an older
 * controller that sends no flag still gets it right).
 */
export function lldpAge(lldp: LldpAgeInput | null | undefined, online: boolean | null | undefined, nowMs: number): LldpAge {
  const then = parseApiTime(lldp?.as_of ?? null);
  const offline = online === false;
  const stale = offline || lldp?.stale === true || then === null;
  if (then === null) return { label: "age unknown", stale };
  const ago = agoLabel(then, nowMs);
  return { label: offline ? `last seen ${ago}` : `as of ${ago}`, stale };
}
